import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, MapPin, Building2, Home, Hotel, Calendar, Users, DollarSign, LogIn, LogOut, User, Scale, Package2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cities } from "@/data/sample-listings";
import { useAuth } from "@/contexts/AuthContext";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/tours", label: "Tours" },
  { href: "/pricing", label: "Pricing" },
  { href: "/tv", label: "TV" },
  { href: "/about", label: "About Us" },
  { href: "/contact", label: "Contact" },
];

const marketplaceItems = [
  {
    title: "List Your Property",
    description: "Get 360° tours for your venue",
    href: "/list-property",
    icon: Building2,
  },
  {
    title: "Owner Portal",
    description: "Manage your listings & leads",
    href: "/owner-portal",
    icon: Home,
  },
  {
    title: "For Agents",
    description: "Complete media packages & tools",
    href: "/agents",
    icon: Users,
  },
  {
    title: "For Developers",
    description: "Estate & property showcase packages",
    href: "/developers",
    icon: Building2,
  },
  {
    title: "Product Photography",
    description: "360 spins for products and showrooms",
    href: "/product-photography",
    icon: Package2,
  },
  {
    title: "Progress Tracking",
    description: "Document construction projects",
    href: "/progress-tracking",
    icon: Calendar,
  },
  {
    title: "Solicitor Hub",
    description: "JV due diligence tools",
    href: "/solicitor",
    icon: Scale,
  },
];

const propertyTypes = [
  { label: "Event Centres", href: "/tours?type=event-centre", icon: Calendar },
  { label: "Hotels", href: "/tours?type=hotel", icon: Hotel },
  { label: "Apartments", href: "/tours?type=apartment", icon: Home },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState("abuja");
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showMarketplace, setShowMarketplace] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const marketplaceRef = useRef<HTMLDivElement>(null);
  const cityRef = useRef<HTMLDivElement>(null);
  const { user, isAdmin, signOut, loading } = useAuth();

  const currentCity = cities.find(c => c.value === selectedCity) || cities[1];

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (marketplaceRef.current && !marketplaceRef.current.contains(event.target as Node)) {
        setShowMarketplace(false);
      }
      if (cityRef.current && !cityRef.current.contains(event.target as Node)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border">
      <nav className="container-tight">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl hero-gradient flex items-center justify-center">
              <span className="text-primary-foreground font-display font-bold text-lg">V</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-display font-bold text-xl text-foreground">Vista360</span>
              <span className="font-display font-bold text-xl text-secondary">.NG</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 rounded-lg font-medium transition-colors hover:bg-muted ${
                  location.pathname === link.href
                    ? "text-primary bg-primary/5"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {isAdmin && (
              <Link
                to="/admin"
                className={`px-4 py-2 rounded-lg font-medium transition-colors hover:bg-muted ${
                  location.pathname === "/admin"
                    ? "text-primary bg-primary/5"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Admin
              </Link>
            )}

            {/* Marketplace Mega Dropdown */}
            <div ref={marketplaceRef} className="relative">
              <button
                onClick={() => setShowMarketplace(!showMarketplace)}
                onMouseEnter={() => setShowMarketplace(true)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium transition-colors hover:bg-muted ${
                  showMarketplace ? "text-primary bg-primary/5" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Marketplace
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showMarketplace ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {showMarketplace && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    onMouseLeave={() => setShowMarketplace(false)}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[540px] bg-card rounded-2xl shadow-premium border border-border overflow-hidden"
                  >
                    <div className="grid grid-cols-2 gap-0">
                      {/* Left Column - For Property Owners */}
                      <div className="p-4 space-y-1 border-r border-border">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-3">
                          For Property Owners
                        </p>
                        {marketplaceItems.slice(0, 2).map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setShowMarketplace(false)}
                            className="flex items-start gap-3 p-3 rounded-xl hover:bg-muted transition-colors group"
                          >
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                              <item.icon className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                                {item.title}
                              </p>
                              <p className="text-sm text-muted-foreground">{item.description}</p>
                            </div>
                          </Link>
                        ))}
                      </div>

                      {/* Right Column - Partner & Professional Access */}
                      <div className="p-4 space-y-1">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-3">
                          Partner & Professional
                        </p>
                        {marketplaceItems.slice(2).map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setShowMarketplace(false)}
                            className="flex items-start gap-3 p-3 rounded-xl hover:bg-muted transition-colors group"
                          >
                            <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary/30 transition-colors">
                              <item.icon className="w-5 h-5 text-secondary" />
                            </div>
                            <div>
                              <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                                {item.title}
                              </p>
                              <p className="text-sm text-muted-foreground">{item.description}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Bottom - Browse by Type */}
                    <div className="bg-muted/50 p-4 border-t border-border">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                        Browse by Property Type
                      </p>
                      <div className="flex gap-2">
                        {propertyTypes.map((type) => (
                          <Link
                            key={type.href}
                            to={type.href}
                            onClick={() => setShowMarketplace(false)}
                            className="flex items-center gap-2 px-4 py-2 rounded-full bg-background border border-border hover:border-primary hover:text-primary transition-colors text-sm font-medium"
                          >
                            <type.icon className="w-4 h-4" />
                            {type.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* City Selector & CTA */}
          <div className="hidden md:flex items-center gap-3">
            {/* City Selector */}
            <div ref={cityRef} className="relative">
              <button
                onClick={() => setShowCityDropdown(!showCityDropdown)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted hover:bg-muted/80 transition-colors"
              >
                <MapPin className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm">{currentCity.label}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showCityDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {showCityDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="absolute top-full right-0 mt-2 w-48 bg-card rounded-xl shadow-premium border border-border overflow-hidden z-50"
                  >
                    {cities.slice(1).map((city) => (
                      <button
                        key={city.value}
                        onClick={() => {
                          setSelectedCity(city.value);
                          setShowCityDropdown(false);
                        }}
                        className={`w-full flex items-center justify-between px-4 py-3 hover:bg-muted transition-colors ${
                          selectedCity === city.value ? 'bg-muted' : ''
                        }`}
                      >
                        <span className="font-medium">{city.label}</span>
                        <span className="text-sm text-muted-foreground">{city.count} tours</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Auth Buttons */}
            {!loading && (
              <>
                {user ? (
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <Link to="/admin">
                        <Button variant="outline" size="sm" className="gap-2">
                          <Scale className="w-4 h-4" />
                          Admin
                        </Button>
                      </Link>
                    )}
                    <Link to="/profile" className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 transition-colors">
                      <User className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium truncate max-w-[120px]">
                        {user.user_metadata?.full_name || user.email?.split('@')[0]}
                      </span>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleSignOut}
                      className="gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Link to="/signin">
                      <Button variant="ghost" size="sm" className="gap-2">
                        <LogIn className="w-4 h-4" />
                        Sign In
                      </Button>
                    </Link>
                    <Link to="/signup">
                      <Button size="sm" className="hero-gradient text-primary-foreground font-semibold">
                        Sign Up
                      </Button>
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-border"
            >
              <div className="max-h-[calc(100vh-5rem)] overflow-y-auto py-4 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                      location.pathname === link.href
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}

                {/* Mobile Marketplace Section */}
                <div className="px-4 pt-4 border-t border-border mt-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Marketplace
                  </p>
                  <div className="space-y-1">
                    {marketplaceItems.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors"
                      >
                        <item.icon className="w-5 h-5 text-primary" />
                        <span className="font-medium">{item.title}</span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Mobile Property Types */}
                <div className="px-4 pt-4 border-t border-border mt-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Browse by Type
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {propertyTypes.map((type) => (
                      <Link
                        key={type.href}
                        to={type.href}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 rounded-full bg-muted hover:bg-primary/10 hover:text-primary transition-colors text-sm font-medium"
                      >
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </Link>
                    ))}
                  </div>
                </div>
                
                {/* Mobile City Selector */}
                <div className="px-4 pt-4 border-t border-border mt-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Select City
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {cities.slice(1).map((city) => (
                      <button
                        key={city.value}
                        onClick={() => setSelectedCity(city.value)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          selectedCity === city.value
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        {city.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mobile Auth Section */}
                <div className="px-4 pt-4 pb-4 border-t border-border mt-4">
                  {!loading && (
                    <>
                      {user ? (
                          <div className="space-y-3">
                          {isAdmin && (
                            <Link to="/admin" onClick={() => setIsOpen(false)}>
                              <Button variant="outline" className="w-full gap-2">
                                <Scale className="w-4 h-4" />
                                Admin
                              </Button>
                            </Link>
                          )}
                          <Link to="/profile" onClick={() => setIsOpen(false)} className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg hover:bg-muted/70 transition-colors">
                            <User className="w-5 h-5 text-primary" />
                            <span className="font-medium">
                              {user.user_metadata?.full_name || user.email?.split('@')[0]}
                            </span>
                          </Link>
                          <Button 
                            variant="outline" 
                            className="w-full gap-2"
                            onClick={() => {
                              handleSignOut();
                              setIsOpen(false);
                            }}
                          >
                            <LogOut className="w-4 h-4" />
                            Sign Out
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Link to="/signin" onClick={() => setIsOpen(false)}>
                            <Button variant="outline" className="w-full gap-2">
                              <LogIn className="w-4 h-4" />
                              Sign In
                            </Button>
                          </Link>
                          <Link to="/signup" onClick={() => setIsOpen(false)}>
                            <Button className="w-full hero-gradient text-primary-foreground font-semibold">
                              Sign Up
                            </Button>
                          </Link>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};
